import type {
  GroupMentoringProductDTO,
  IndividualMentoringProductDTO,
  ProjectMentoringProductDTO
} from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto'
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types'
import {
  calculateGroupMentoringPrice,
  calculateIndividualMentoringPrice,
  calculateMentoringSubscriptionPrice,
  calculateProjectMentoringPrice
} from '../../../prices'

export type MentoringOfferProduct =
  | GroupMentoringProductDTO
  | IndividualMentoringProductDTO
  | ProjectMentoringProductDTO

type TermsSummaryArgs = {
  mentoringTypeLabel: string
  billingCadenceLabel: string
  /** Only extended mentoring carries hours; everything else leaves it empty. */
  hoursWithMentor: number | null
}

/**
 * One line naming what is being billed - mentoring, cadence, hours. The labels
 * always arrive ready-made (from the query for stored terms, from the shared
 * enum descriptions for the terms picked in the card above), so nothing is
 * translated or rebuilt here.
 */
export function formatTermsSummary({
  mentoringTypeLabel,
  billingCadenceLabel,
  hoursWithMentor
}: TermsSummaryArgs): string {
  const parts = [mentoringTypeLabel, billingCadenceLabel]

  if (hoursWithMentor !== null) {
    parts.push(`${hoursWithMentor} h z mentorem`)
  }

  return parts.join(' - ')
}

const zlotyFormatter = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
})

/** Whole złoty out of the price book, e.g. "6 149 zł". */
export function formatZloty(zloty: number): string {
  return `${zlotyFormatter.format(zloty)} zł`
}

/** The backend records the frozen price in grosze; staff reads złoty. */
export function formatGrosze(grosze: number): string {
  return formatZloty(grosze / 100)
}

function readPricePerFourWeeks(mentoringProduct: MentoringOfferProduct): number {
  if (mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING) {
    return calculateIndividualMentoringPrice({ hoursWithMentor: mentoringProduct.hoursWithMentor })
  }

  if (mentoringProduct.order === ORDER_TYPE.GROUP_MENTORING) {
    return calculateGroupMentoringPrice()
  }

  return calculateProjectMentoringPrice()
}

/**
 * What one renewal will charge for the terms picked in the card above - the same
 * price book, the same discount and the same rounding the visible card uses, so
 * the confirmation cannot quote a different number than the card. The binding
 * amount is still the one the mutation freezes when staff confirms.
 */
export function calculateRenewalPriceZloty(mentoringProduct: MentoringOfferProduct): number {
  return calculateMentoringSubscriptionPrice(
    readPricePerFourWeeks(mentoringProduct),
    mentoringProduct.subscription
  ).discountedPrice
}
